import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { api } from "@/api";
import { captureAttribution, getAttribution } from "@/attribution";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FloatingCTA } from "@/components/FloatingCTA";
import Home from "@/pages/Home";
import BoardAndTrain from "@/pages/BoardAndTrain";
import PrivateTraining from "@/pages/PrivateTraining";
import VirtualLessons from "@/pages/VirtualLessons";
import Jumpstart from "@/pages/Jumpstart";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Gallery from "@/pages/Gallery";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Programs from "@/pages/Programs";
import About from "@/pages/About";
import CityLanding from "@/pages/CityLanding";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import "@/index.css";

const ScrollAndTrack = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    captureAttribution();
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
    if (!pathname.startsWith("/admin")) {
      api.post("/analytics/track", { page: pathname, referrer: document.referrer, ...getAttribution() }).catch(() => {});
    }
  }, [pathname]);
  return null;
};

const MarketingLayout = () => (
  <>
    <Navbar />
    <Outlet />
    <Footer />
    <FloatingCTA />
  </>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin" replace />;
  return children;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 1.05, touchMultiplier: 1.4, anchors: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollAndTrack />
          <Toaster position="top-right" theme="dark" richColors />
          <Routes>
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services/board-and-train" element={<BoardAndTrain />} />
              <Route path="/services/private-training" element={<PrivateTraining />} />
              <Route path="/virtual-dog-training" element={<VirtualLessons />} />
              <Route path="/services/jumpstart" element={<Jumpstart />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faqs" element={<Faq />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/about" element={<About />} />
              <Route path="/dog-training/:citySlug" element={<CityLanding />} />
            </Route>
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
